import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import '../styles/AiChatbot.css';

const PLAN_LIMITS = { free: 50, mate: 1000, premium: Infinity };

const WELCOME_MSG = {
  id: 'welcome',
  role: 'assistant',
  text: '¡Hola! Soy Cupido con Mate 🧉💘 Tu asistente para encontrar el amor en San Juan. ¿En qué te puedo ayudar? Te cuento cómo usar cada parte de la app, te doy ideas para romper el hielo y mejorar tu perfil, y te banco cuando el amor se pone difícil. 💛',
};

// En el panel de comercios (modo="comercio") es el asistente de Cupido Delivery:
// funciona con o sin sesión, sin límite por plan y con otras preguntas rápidas.
const WELCOME_COMERCIO = {
  id: 'welcome',
  role: 'assistant',
  text: '¡Hola! Soy el asistente de Cupido Delivery 🎁 Te ayudo a registrar tu comercio, cargar productos, marcar tu zona y tu horario, conectar Mercado Pago y entregar los pedidos. ¿En qué te ayudo?',
};
const WELCOME_DESCUENTOS = {
  id: 'welcome',
  role: 'assistant',
  text: '¡Hola! Soy el asistente de Cupido Descuentos 🏷️ Te ayudo a registrar tu local, publicar descuentos para citas y validar los códigos que te muestran las parejas. ¿En qué te ayudo?',
};
const PREGUNTAS_DESCUENTOS = [
  '¿Cómo registro mi local?',
  '¿Cómo publico un descuento?',
  '¿Cómo valido un código?',
  '¿Por qué no aparezco?',
  '¿Qué es Cita segura?',
];
const PREGUNTAS = [
  '¿Cómo rompo el hielo?',
  'Mejorá mi bio',
  '¿Dónde está la ayuda?',
  'Nadie me escribe, ¿qué hago?',
  '¿Cómo uso las notas y la afinidad?',
  '¿Dónde voy en la primera cita?',
  '¿Cómo funciona Cita segura?',
];
const PREGUNTAS_COMERCIO = [
  '¿Qué me falta para vender?',
  '¿Por qué no aparezco en Regalos y +?',
  '¿Cómo marco mi zona de entrega?',
  '¿Cómo cobro con Mercado Pago?',
  '¿Cómo entrego un pedido?',
];

export default function AiChatbot({ modo }) {
  // Panel de Cupido Descuentos: mismo asistente de comercios, con su propia marca, bienvenida y preguntas
  const descuentos = modo === 'descuentos';
  const comercio = modo === 'comercio' || descuentos;
  const marcaNombre = descuentos ? 'Cupido Descuentos' : 'Cupido Delivery';
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // En una conversación del chat el botón sube para no tapar el botón de enviar
  const enConversacion = /^\/chat\/./.test(useLocation().pathname);
  const [messages, setMessages] = useState([descuentos ? WELCOME_DESCUENTOS : comercio ? WELCOME_COMERCIO : WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { used, limit, remaining }
  const [error, setError] = useState('');
  const [fabPos, setFabPos] = useState(null); // null = CSS default; { top, left } when dragged
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const dragInfo = useRef({ active: false, startX: 0, startY: 0, fabLeft: 0, fabTop: 0, moved: false });

  // ── Drag-to-move FAB ──────────────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      const d = dragInfo.current;
      if (!d.active) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = cx - d.startX;
      const dy = cy - d.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
      if (!d.moved) return;
      if (e.cancelable) e.preventDefault();
      const sz = 56;
      const left = Math.max(8, Math.min(window.innerWidth - sz - 8, d.fabLeft + dx));
      const top = Math.max(8, Math.min(window.innerHeight - sz - 8, d.fabTop + dy));
      setFabPos({ left, top });
    };
    const onUp = () => {
      const d = dragInfo.current;
      if (d.active && !d.moved) setOpen(o => !o); // tap/click
      d.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const onFabDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const rect = fabRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragInfo.current = { active: true, startX: cx, startY: cy, fabLeft: rect.left, fabTop: rect.top, moved: false };
  };

  // Compute FAB inline style (overrides CSS when dragged)
  const fabStyle = fabPos
    ? { top: fabPos.top, left: fabPos.left, bottom: 'auto', right: 'auto' }
    : {};

  // Compute panel position relative to FAB when dragged
  const panelStyle = (() => {
    if (!fabPos) return {};
    const panelW = 360, panelH = 570, sz = 56, gap = 10;
    const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, fabPos.left - (panelW - sz)));
    const top = fabPos.top > panelH + gap + 20
      ? fabPos.top - panelH - gap
      : fabPos.top + sz + gap;
    return { top, left, bottom: 'auto', right: 'auto' };
  })();

  // ── Load usage status ─────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    if (comercio) return;
    try {
      const data = await api.aiStatus();
      setStatus(data);
    } catch (_) {}
  }, [comercio]);

  useEffect(() => {
    if (open) {
      loadStatus();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, loadStatus]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ──────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');

    // Check limit client-side (null = ilimitado para premium)
    if (!isUnlimited && status && status.remaining !== null && status.remaining <= 0) {
      setError(`Llegaste al límite de ${status.limit} mensajes semanales de tu plan. ¡Mejorá tu plan para chatear más!`);
      return;
    }

    const userMsg = { id: Date.now(), role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    // Build history for API (exclude welcome, last 10 turns)
    const history = messages
      .filter(m => m.id !== 'welcome')
      .slice(-10)
      .map(m => ({ role: m.role, content: m.text }));

    try {
      const data = comercio ? await api.comercioAsistente(text, history, descuentos ? 'descuentos' : undefined) : await api.aiChat(text, history);
      const aiMsg = { id: Date.now() + 1, role: 'assistant', text: data.reply, ayuda: data.ayuda || null };
      setMessages(prev => [...prev, aiMsg]);
      // Update status after each message
      if (data.remaining !== undefined) {
        setStatus(prev => ({
          ...prev,
          used: (prev?.used || 0) + 1,
          remaining: data.remaining,
        }));
      } else {
        loadStatus();
      }
    } catch (e) {
      setError(e.message || 'Error conectando con el asistente.');
      // Remove optimistic user message on hard error
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Plan limits ────────────────────────────────────────────────────────
  const planLimit = PLAN_LIMITS[user?.plan] || 50;
  const isUnlimited = comercio || user?.plan === 'premium';
  const usedCount = status?.used ?? 0;
  const remainingCount = status?.remaining ?? (isUnlimited ? Infinity : planLimit - usedCount);
  const pct = isUnlimited ? 100 : Math.max(0, Math.round((remainingCount / planLimit) * 100));
  const limitReached = !isUnlimited && remainingCount <= 0;

  return (
    <>
      {/* ── Floating button (arrastrá para mover) ───────────────────── */}
      <button
        ref={fabRef}
        className={`ai-fab ${open ? 'ai-fab--open' : ''} ${enConversacion && !open ? 'ai-fab--alto' : ''}`}
        style={fabStyle}
        onMouseDown={onFabDown}
        onTouchStart={onFabDown}
        title={`${comercio ? marcaNombre : 'Cupido con Mate'} — Asistente IA (arrastrá para mover)`}
        aria-label="Abrir asistente IA">
        {open ? '✕' : '🧉'}
        {!open && status && !isUnlimited && remainingCount < 10 && remainingCount > 0 && (
          <span className="ai-fab-badge">{remainingCount}</span>
        )}
      </button>

      {/* ── Chat panel ──────────────────────────────────────────────── */}
      {open && (
        <div className="ai-panel" style={panelStyle}>
          {/* Header */}
          <div className="ai-panel-header">
            <div className="ai-panel-avatar">🧉</div>
            <div className="ai-panel-title">
              <div className="ai-panel-name">{comercio ? marcaNombre : 'Cupido con Mate'}</div>
              <div className="ai-panel-subtitle">{descuentos ? 'Asistente IA para locales' : comercio ? 'Asistente IA para comercios' : `Asistente IA · Plan ${user?.plan || 'free'}`}</div>
            </div>
            <button className="ai-panel-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          {/* Usage bar */}
          {!isUnlimited && (
            <div className="ai-usage-bar">
              <div className="ai-usage-track">
                <div
                  className={`ai-usage-fill ${pct < 20 ? 'ai-usage-fill--low' : ''}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="ai-usage-label">
                {limitReached
                  ? '⚠️ Límite semanal alcanzado'
                  : `${remainingCount} mensajes restantes esta semana`}
              </span>
            </div>
          )}

          {/* Messages */}
          <div className="ai-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`ai-msg ai-msg--${msg.role}`}>
                {msg.role === 'assistant' && (
                  <div className="ai-msg-avatar">🧉</div>
                )}
                <div className="ai-msg-bubble">
                  {msg.text.split('\n').map((line, i) => (
                    <React.Fragment key={i}>{line}{i < msg.text.split('\n').length - 1 && <br />}</React.Fragment>
                  ))}
                  {msg.ayuda && (
                    <a className="ai-ayuda-link" href={msg.ayuda.ruta}
                      onClick={e => { e.preventDefault(); setOpen(false); navigate(msg.ayuda.ruta); }}>
                      📖 Ver en Ayuda: {msg.ayuda.titulo}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="ai-msg ai-msg--assistant">
                <div className="ai-msg-avatar">🧉</div>
                <div className="ai-msg-bubble ai-msg-bubble--typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            {error && (
              <div className="ai-error">{error}</div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Upgrade banner when limit reached */}
          {limitReached && (
            <div className="ai-upgrade-banner">
              <p>🚀 ¡Conseguí más mensajes con un plan superior!</p>
              <a href="/planes" className="btn btn-primary btn-sm">Ver planes</a>
            </div>
          )}

          {/* Input */}
          <div className="ai-input-area">
            <textarea
              ref={inputRef}
              className="ai-input"
              placeholder={limitReached ? 'Límite semanal alcanzado…' : comercio ? 'Preguntale al asistente…' : 'Preguntale a Cupido con Mate…'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={500}
              disabled={sending || limitReached}
            />
            <button
              className="ai-send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || sending || limitReached}
              title="Enviar">
              {sending ? '⏳' : '➤'}
            </button>
          </div>

          {/* Quick prompts */}
          {messages.length <= 1 && !limitReached && (
            <div className="ai-quick-prompts">
              {(descuentos ? PREGUNTAS_DESCUENTOS : comercio ? PREGUNTAS_COMERCIO : PREGUNTAS).map(prompt => (
                <button
                  key={prompt}
                  className="ai-quick-btn"
                  onClick={() => { setInput(prompt); setTimeout(() => inputRef.current?.focus(), 0); }}>
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
